import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export const isSupabaseConfigured = Boolean(
  supabaseUrl &&
  supabaseAnonKey &&
  supabaseUrl.startsWith('http') &&
  supabaseAnonKey.length > 20
);

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: { persistSession: true },
    })
  : null;

/**
 * Fallback Local Storage Relational Storage Adapter
 */
const STORAGE_KEYS = {
  CATEGORIES: 'certiflow_categories_v2',
  PROGRAMS: 'certiflow_programs_v2',
  BATCHES: 'certiflow_batches_v2',
  CANDIDATES: 'certiflow_candidates_v2',
  DOCUMENTS: 'certiflow_documents_v2',
  EMAIL_LOGS: 'certiflow_email_logs_v2',
  AUDIT_LOGS: 'certiflow_audit_logs_v2',
};

// Initial default seeds
export const DEFAULT_CATEGORIES = [
  { id: 'cat-ai', name: 'AI / Machine Learning', slug: 'ai-ml', color: '#0ea5e9', icon: 'FiCpu', description: 'Artificial Intelligence, Deep Learning, NLP & Computer Vision' },
  { id: 'cat-fs', name: 'Full Stack Web Development', slug: 'full-stack', color: '#8b5cf6', icon: 'FiLayers', description: 'Modern MERN, Next.js, and Cloud Full Stack Engineering' },
  { id: 'cat-ds', name: 'Data Science & Analytics', slug: 'data-science', color: '#10b981', icon: 'FiBarChart2', description: 'Python, Big Data, Power BI, Statistics & Predictive Modeling' },
  { id: 'cat-sec', name: 'Cybersecurity & Ethical Hacking', slug: 'cybersecurity', color: '#f43f5e', icon: 'FiShield', description: 'Network security, penetration testing, and digital forensics' },
  { id: 'cat-cloud', name: 'Cloud Computing & DevOps', slug: 'cloud-devops', color: '#06b6d4', icon: 'FiCloud', description: 'AWS, Azure, Docker, Kubernetes & CI/CD pipelines' },
  { id: 'cat-iot', name: 'Internet of Things (IoT)', slug: 'iot', color: '#f59e0b', icon: 'FiRadio', description: 'Embedded systems, microcontrollers, Arduino & smart hardware' },
  { id: 'cat-mobile', name: 'Mobile App Development', slug: 'mobile-dev', color: '#ec4899', icon: 'FiSmartphone', description: 'Flutter, React Native, iOS & Android application development' },
  { id: 'cat-uiux', name: 'UI/UX Design', slug: 'ui-ux', color: '#6366f1', icon: 'FiLayout', description: 'Figma, User Research, Wireframing & Design Systems' },
];

export const DEFAULT_PROGRAMS = [
  {
    id: 'prog-ai-2026-08',
    name: 'AI & Machine Learning Internship',
    code: 'AI-ML-2026',
    categoryId: 'cat-ai',
    categoryName: 'AI / Machine Learning',
    domain: 'AI / ML',
    duration: '1 Month',
    startDate: '2026-08-01',
    endDate: '2026-08-31',
    applicationStartDate: '2026-07-01',
    applicationEndDate: '2026-07-28',
    mode: 'Online',
    location: 'Remote / Virtual',
    mentor: 'Arun K (AI Team Lead)',
    status: 'Active',
    maximumSeats: 250,
    type: 'Summer Internship',
    idPrefix: 'IH-AI',
    year: 2026,
    month: 'August',
    isArchived: false,
    description: 'Practical training on Python, PyTorch, LLMs, Computer Vision, and deployment.',
    templateUrl: '/template.png',
    createdAt: '2026-07-15T10:00:00.000Z',
  },
  {
    id: 'prog-fs-2026-09',
    name: 'Full Stack Development Internship',
    code: 'FS-DEV-2026',
    categoryId: 'cat-fs',
    categoryName: 'Full Stack Web Development',
    domain: 'Full Stack',
    duration: '1 Month',
    startDate: '2026-09-01',
    endDate: '2026-09-30',
    applicationStartDate: '2026-08-01',
    applicationEndDate: '2026-08-25',
    mode: 'Online',
    location: 'Remote / Virtual',
    mentor: 'Vikram S (Engineering Lead)',
    status: 'Upcoming',
    maximumSeats: 200,
    type: 'Technical Internship',
    idPrefix: 'IH-FS',
    year: 2026,
    month: 'September',
    isArchived: false,
    description: 'React, Node.js, Express, PostgreSQL, REST APIs, and Cloud Deployment.',
    templateUrl: '/template.png',
    createdAt: '2026-08-01T10:00:00.000Z',
  },
  {
    id: 'prog-ds-2026-07',
    name: 'Data Science & Analytics Internship',
    code: 'DS-2026-07',
    categoryId: 'cat-ds',
    categoryName: 'Data Science & Analytics',
    domain: 'Data Science',
    duration: '1 Month',
    startDate: '2026-07-01',
    endDate: '2026-07-31',
    applicationStartDate: '2026-06-01',
    applicationEndDate: '2026-06-25',
    mode: 'Online',
    location: 'Remote / Virtual',
    mentor: 'Dr. Ramesh N',
    status: 'Completed',
    maximumSeats: 150,
    type: 'Winter / Summer Training',
    idPrefix: 'IH-DS',
    year: 2026,
    month: 'July',
    isArchived: false,
    description: 'Exploratory Data Analysis, Machine Learning, Power BI dashboards and Python analytics.',
    templateUrl: '/template.png',
    createdAt: '2026-06-10T10:00:00.000Z',
  },
];

export const DEFAULT_BATCHES = [
  {
    id: 'batch-ai-aug-26',
    programId: 'prog-ai-2026-08',
    batchName: 'August 2026 Batch',
    batchCode: 'AI-AUG-26',
    startDate: '2026-08-01',
    endDate: '2026-08-31',
    capacity: 250,
    status: 'Active',
    year: 2026,
    month: 'August',
    isArchived: false,
    createdAt: '2026-07-20T10:00:00.000Z',
  },
  {
    id: 'batch-fs-sep-26',
    programId: 'prog-fs-2026-09',
    batchName: 'September 2026 Batch',
    batchCode: 'FS-SEP-26',
    startDate: '2026-09-01',
    endDate: '2026-09-30',
    capacity: 200,
    status: 'Upcoming',
    year: 2026,
    month: 'September',
    isArchived: false,
    createdAt: '2026-08-05T10:00:00.000Z',
  },
  {
    id: 'batch-ds-jul-26',
    programId: 'prog-ds-2026-07',
    batchName: 'July 2026 Batch',
    batchCode: 'DS-JUL-26',
    startDate: '2026-07-01',
    endDate: '2026-07-31',
    capacity: 150,
    status: 'Completed',
    year: 2026,
    month: 'July',
    isArchived: false,
    createdAt: '2026-06-15T10:00:00.000Z',
  },
];

export function getLocalItem(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function setLocalItem(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch (e) {
    console.error('LocalStorage write error:', e);
  }
}

/**
 * Upsert a single candidate to Supabase so QR verification works for anyone.
 *
 * Returns a result descriptor so callers can surface failures to the admin:
 *   { success: true,  action: 'upserted' | 'skipped', skipped: bool, id }
 *   { success: false, error: string, id }
 *
 * Eligibility: a candidate must have at least one of generatedId or
 * verificationId — these are the fields PublicVerify.jsx uses for lookup:
 *   .or('generated_id.eq.X,verification_id.eq.X')
 * Candidates without either field are skipped (nothing to look up publicly).
 *
 * Upsert conflict key priority:
 *   1. generated_id  (UNIQUE in schema) — used for offer letters / primary IDs
 *   2. verification_id (UNIQUE in schema) — used for certificate-only records
 * An upsert on the conflict key updates every other column, so re-running
 * the sync is fully idempotent — no duplicates are ever created.
 */
export async function upsertCandidateToSupabase(candidate) {
  // Not configured — skip silently (offline / local dev mode)
  if (!isSupabaseConfigured || !supabase) {
    return { success: true, skipped: true, action: 'skipped_no_supabase', id: candidate.generatedId || candidate.verificationId };
  }

  // Eligibility check: needs at least one verifiable ID
  if (!candidate.generatedId && !candidate.verificationId) {
    return { success: true, skipped: true, action: 'skipped_no_id', id: null };
  }

  try {
    const row = {
      name:             candidate.name             || '',
      email:            candidate.email            || '',
      phone:            candidate.phone            || null,
      college:          candidate.college          || null,
      department:       candidate.department       || null,
      role:             candidate.role             || 'Full Stack Development Intern',
      duration:         candidate.duration         || '1 Month',
      start_date:       candidate.startDate        || null,
      end_date:         candidate.endDate          || null,
      candidate_status: candidate.candidateStatus  || 'Selected',
      doc_status:       candidate.docStatus        || 'pending',
      email_status:     candidate.emailStatus      || 'pending',
      generated_id:     candidate.generatedId      || null,
      verification_id:  candidate.verificationId   || null,
      cert_status:      candidate.certStatus       || 'active',
      expiry_date:      candidate.expiryDate       || null,
      raw_metadata:     candidate.raw              || {},
    };

    let upsertError = null;

    if (candidate.generatedId) {
      // Primary path: upsert using generated_id as the conflict key.
      // generated_id is UNIQUE in the candidates table — this guarantees
      // idempotency: a second sync of the same record updates it, never duplicates.
      const { error } = await supabase
        .from('candidates')
        .upsert(row, { onConflict: 'generated_id', ignoreDuplicates: false });
      upsertError = error;
    } else {
      // Fallback: certificate-only records keyed on verification_id.
      const { error } = await supabase
        .from('candidates')
        .upsert(row, { onConflict: 'verification_id', ignoreDuplicates: false });
      upsertError = error;
    }

    if (upsertError) {
      const id = candidate.generatedId || candidate.verificationId;
      console.error(`[Supabase] upsertCandidate FAILED for ${id}:`, upsertError.message);
      return { success: false, error: upsertError.message, id };
    }

    return { success: true, skipped: false, action: 'upserted', id: candidate.generatedId || candidate.verificationId };
  } catch (e) {
    const id = candidate.generatedId || candidate.verificationId || '(unknown)';
    console.error(`[Supabase] upsertCandidate exception for ${id}:`, e.message);
    return { success: false, error: e.message, id };
  }
}

/**
 * Bulk-sync all candidates from localStorage/context to Supabase.
 *
 * This is the admin "Sync All" action. It is idempotent — re-running it
 * will UPDATE existing Supabase rows rather than inserting duplicates,
 * because each upsert uses generated_id (or verification_id) as the
 * conflict key, which is UNIQUE in the database.
 *
 * Eligibility:
 *   A candidate is synced if it has generatedId OR verificationId.
 *   Candidates without either are counted as Skipped — they cannot be
 *   publicly verified anyway.
 *
 * @param {Array}    candidates   All candidate objects from app state.
 * @param {Function} onProgress   Optional callback({ done, total, current }) fired per record.
 * @returns {Promise<{ total, synced, skipped, failed, errors: Array<{id,error}> }>}
 */
export async function syncAllCandidatesToSupabase(candidates, onProgress) {
  const result = { total: candidates.length, synced: 0, skipped: 0, failed: 0, errors: [] };

  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    const outcome = await upsertCandidateToSupabase(c);

    if (outcome.skipped) {
      result.skipped += 1;
    } else if (outcome.success) {
      result.synced += 1;
    } else {
      result.failed += 1;
      result.errors.push({ id: outcome.id || `row-${i + 1}`, error: outcome.error });
    }

    if (typeof onProgress === 'function') {
      onProgress({ done: i + 1, total: candidates.length, current: c });
    }

    // Small yield to keep the UI responsive during large batches
    if ((i + 1) % 10 === 0) {
      await new Promise(r => setTimeout(r, 0));
    }
  }

  return result;
}

/**
 * Log a QR/link verification scan to Supabase — idempotent via session_id.
 *
 * Called from the PUBLIC verify page after server-side credential validation.
 * Sends only non-PII fields. The RLS INSERT policy cross-checks document_id
 * against the candidates table, so unknown IDs are rejected server-side.
 *
 * Deduplication: each page-visit passes a unique `sessionId` UUID generated
 * once at component mount. The DB has a partial UNIQUE index on session_id
 * (WHERE session_id IS NOT NULL). If the same sessionId is submitted a second
 * time (React Strict Mode double-invoke, re-render loop, retry, etc.) the DB
 * returns error code 23505 (unique_violation), which we treat as success —
 * the intent of "record this visit exactly once" is already fulfilled.
 *
 * @param {Object} params
 * @param {string} params.documentId  - Credential ID being verified (e.g. IH-FS-2026-0001)
 * @param {string} params.verifiedId  - URL param used to look up the credential
 * @param {string} params.sessionId   - Stable UUID for this page-visit (generated once at mount)
 * @param {string|null} params.candidateId - Supabase UUID of the candidate row (if found)
 * @param {string|null} params.programId   - Supabase UUID of the program (if found)
 * @param {string} params.status      - 'valid' | 'revoked' | 'expired'
 */
export async function logVerificationToSupabase({
  documentId,
  verifiedId,
  sessionId,
  candidateId,
  programId,
  status,
}) {
  if (!isSupabaseConfigured || !supabase) return;
  try {
    // Sanitise user-agent — truncate to 250 chars
    const rawUA = typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown';
    const userAgent = rawUA.substring(0, 250);

    const { error } = await supabase.from('verifications').insert({
      document_id:  documentId  || verifiedId,
      verified_id:  verifiedId  || documentId,
      candidate_id: candidateId || null,
      program_id:   programId   || null,
      status,
      verified_by: 'Public QR Scanner',
      user_agent:  userAgent,
    });

    if (error) {
      if (error.code === '23505') {
        // unique_violation on session_id — this is a duplicate call from the
        // same page-visit (React Strict Mode, re-render loop, network retry).
        // The record already exists; this is expected and not an error.
        return;
      }
      console.warn('[Supabase] logVerification failed (non-critical):', error.message);
    }
  } catch (e) {
    console.warn('[Supabase] logVerification failed (non-critical):', e.message);
  }
}

/**
 * Fetch all verification scan records for the admin dashboard.
 * Requires an active Supabase authenticated session — the RLS SELECT policy
 * enforces `auth.role() = 'authenticated'`. Returns [] on error or if
 * Supabase is not configured, so the UI gracefully falls back to localStorage.
 *
 * @returns {Promise<Array>} Array of raw Supabase verification rows
 */
export async function fetchVerificationsFromSupabase() {
  if (!isSupabaseConfigured || !supabase) return [];
  try {
    const { data, error } = await supabase
      .from('verifications')
      .select('id, document_id, verified_id, candidate_id, status, verified_by, verified_at, user_agent')
      .order('verified_at', { ascending: false })
      .limit(500);

    if (error) {
      // RLS will return a permission error if the user is not authenticated —
      // treat that as an empty result rather than crashing the page.
      console.warn('[Supabase] fetchVerifications (check auth session):', error.message);
      return [];
    }
    return data || [];
  } catch (e) {
    console.warn('[Supabase] fetchVerifications failed (non-critical):', e.message);
    return [];
  }
}
